/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { PluginHeaderProps } from './types';
import { Tabs, TabList, Tab } from '../Tabs';
import { useDefinition } from '../../hooks/useDefinition';
import { PluginHeaderDefinition } from './definition';
import { type NavigateOptions } from 'react-router-dom';
import { Children, useMemo, useRef } from 'react';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
import { Box } from '../Box';
import { Link } from '../Link';
import { RiShapesLine } from '@remixicon/react';
import { Text } from '../Text';
import { VisuallyHidden } from '../VisuallyHidden';
import { PluginHeaderBreadcrumbs } from './PluginHeaderBreadcrumbs';

declare module 'react-aria-components' {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

/**
 * Renders a plugin header with icon, title, custom actions, and optional tabs.
 * Always participates in the background context system so descendants (e.g. buttons)
 * get the correct `data-on-bg` styling inside the toolbar and tabs.
 *
 * @public
 */
export const PluginHeader = (props: PluginHeaderProps) => {
  const { ownProps } = useDefinition(PluginHeaderDefinition, props);
  const {
    classes,
    tabs,
    icon,
    title,
    titleLink,
    breadcrumbs,
    customActions,
    onTabSelectionChange,
  } = ownProps;

  const hasTabs = tabs && tabs.length > 0;
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const rootRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastAppliedHeightRef = useRef<number | undefined>(undefined);

  const actionChildren = useMemo(() => {
    return Children.toArray(customActions);
  }, [customActions]);

  useIsomorphicLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return undefined;
    }

    const cancelScheduledUpdate = () => {
      if (animationFrameRef.current === undefined) {
        return;
      }

      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    };

    const applyHeight = (height: number) => {
      if (lastAppliedHeightRef.current === height) {
        return;
      }

      lastAppliedHeightRef.current = height;
      document.documentElement.style.setProperty(
        '--bui-header-height',
        `${height}px`,
      );
    };

    const scheduleHeightUpdate = () => {
      cancelScheduledUpdate();
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = undefined;
        applyHeight(el.offsetHeight);
      });
    };

    // Set height once immediately so the initial layout is correct.
    applyHeight(el.offsetHeight);

    // Observe for resize changes if ResizeObserver is available
    // (not present in Jest/jsdom by default)
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        cancelScheduledUpdate();
        lastAppliedHeightRef.current = undefined;
        document.documentElement.style.removeProperty('--bui-header-height');
      };
    }

    const observer = new ResizeObserver(() => {
      scheduleHeightUpdate();
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelScheduledUpdate();
      lastAppliedHeightRef.current = undefined;
      document.documentElement.style.removeProperty('--bui-header-height');
    };
  }, []);

  const titleText = title || 'Your plugin';

  return (
    <div ref={rootRef} className={classes.root}>
      <div className={classes.toolbar} data-has-tabs={hasTabs ? '' : undefined}>
        <div className={classes.toolbarContent}>
          <Box bg="neutral" className={classes.toolbarIcon} aria-hidden="true">
            {icon || <RiShapesLine />}
          </Box>
          {hasBreadcrumbs ? (
            <div className={classes.toolbarName}>
              <VisuallyHidden>
                <h1>{titleText}</h1>
              </VisuallyHidden>
              <PluginHeaderBreadcrumbs
                entries={breadcrumbs}
                className={classes.breadcrumbs}
                ellipsisClassName={classes.breadcrumbsEllipsis}
              />
            </div>
          ) : (
            <h1 className={classes.toolbarName}>
              {titleLink ? (
                <Link href={titleLink} standalone variant="body-medium">
                  {titleText}
                </Link>
              ) : (
                <Text as="span" variant="body-medium">
                  {titleText}
                </Text>
              )}
            </h1>
          )}
        </div>
        <div className={classes.toolbarControls}>{actionChildren}</div>
      </div>
      {hasTabs && (
        <div className={classes.tabs}>
          <Tabs onSelectionChange={onTabSelectionChange}>
            <TabList>
              {tabs?.map(tab => (
                <Tab
                  key={tab.id}
                  id={tab.id}
                  href={tab.href}
                  matchStrategy={tab.matchStrategy}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </div>
      )}
    </div>
  );
};
